import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useHistory } from "react-router-dom";
import { AuthService } from "../service/AuthService";
import { setSavedState } from "../utils/LocalStorage.utils";
import LSVariables from "../configs/LS_variables.json";
import logo from "../assets/logo-prodrive.svg";
import driverLoginBg from "../assets/login-bg.svg";

const Login: React.FC = () => {
  const [isLoadingLogin, setIsLoginLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const authService = new AuthService();

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Veuillez entrer une adresse e-mail valide")
      .required("L'e-mail est requis"),
    password: yup.string().required("Le mot de passe est requis"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsLoginLoading(true);
      try {
        const res = await authService.logIn(values);
        if (res && res.data) {
          setSavedState(res.data.data, LSVariables.saved_user);
          history.push("/drivers");
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            "Une erreur s'est produite lors de la connexion"
        );
      } finally {
        setIsLoginLoading(false);
      }
    },
  });

  return (
    <div className="flex h-screen">
      {/* Right side - Login Form */}
      <div className="md:w-1/2 mx-auto md:m flex items-center justify-center">
        <div className="w-full max-w-[500px]">
          <div className="py-12">
            <div className="text-center mb-8">
              <img
                src={logo}
                alt="ProDrive Logo"
                className="mx-auto h-12 mb-4"
              />
              <h2 className="text-2xl font-semibold text-gray-800">
                Connexion
              </h2>
            </div>
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div>
                <input
                  id="email"
                  type="email"
                  placeholder="E-mail"
                  autoComplete="email"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("email")}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div>
                <input
                  id="password"
                  type="password"
                  placeholder="Mot de passe"
                  autoComplete="current-password"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("password")}
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              {error && <p className="text-sm p-error">{error}</p>}

              <div>
                <button
                  type="submit"
                  disabled={isLoadingLogin}
                  className="w-full py-4 px-4 border border-transparent rounded-2xl shadow-sm text-md font-medium text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light transition duration-150 ease-in-out"
                >
                  {isLoadingLogin ? "Connexion en cours..." : "Se connecter"}
                </button>
              </div>
              {/*Signup link*/}
              <div>
                <p className="text-sm text-gray-500 w-full text-center">
                  Pas encore de compte ?{" "}
                  <Link
                    to="/signup"
                    className="text-primary hover:underline transition duration-150 ease-in-out"
                  >
                    Inscription
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Added interface transporteur text and copyright */}
          <div className="text-center mt-8">
            <p className="text-lg font-semibold text-gray-700">
              Interface transporteur
            </p>
            <p className="text-sm text-gray-500 mt-2">
              &copy; {new Date().getFullYear()} Master D. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
      <div className="md:flex md:w-1/2 overflow-clip bg-slate-50">
        <img src={driverLoginBg} alt="" className="object-cover scale-125" />
      </div>
    </div>
  );
};

export default Login;
